import React from 'react'
import { WarfishClassicsMapRow } from '../map/warfish/WarfishClassicsNote'
import { isWarfishClassicsMap } from '../map/warfish/presentation'
import { Text, View } from 'react-native'
import type { GameConfig, MapDef } from '@warcarp/api-types'
import { teamsConfigOf } from '@warcarp/api-types'
import { BOOT_TIMER_OPTIONS, cardBonusScheduleInfo, fogOfWarOf, gameConfigWithDefaults } from '@warcarp/api-types'
import { teamListLabel, teamsRuleValue } from '../../lib/teamSetup'

interface RowSpec {
  label: string
  value: string
  hint?: string
  last?: boolean
}

function ConfigRow({ label, value, hint, last = false }: RowSpec) {
  return (
    <View className={`flex-row items-baseline gap-2 py-2 ${last ? '' : 'border-b border-olive/30'}`}>
      <Text className="font-typed text-ink-soft text-sm flex-1">{label}</Text>
      <View className="flex-shrink-0 items-end">
        <Text className="font-stencil text-ink text-sm">{value}</Text>
        {hint && (
          <Text className="font-typed text-ink-soft text-[10px] italic mt-0.5">
            {hint}
          </Text>
        )}
      </View>
    </View>
  )
}

export function GameConfigContent({
  config,
  map,
  players,
  bare = false,
  bootAfterSeconds,
}: {
  config?: Partial<GameConfig>
  map?: MapDef | null
  players: string
  bootAfterSeconds?: number
  /** The containing modal supplies the paper surface and border itself. */
  bare?: boolean
}) {
  const effective = gameConfigWithDefaults(config)
  const startingTroops = effective.startingTroops
  const autoAssign = effective.autoAssignTerritories
  const maxAttacks = effective.maxAttacksPerTurn
  const maxFortifies = effective.maxFortifiesPerTurn
  const eliminateBonus = effective.eliminatePlayerBonus
  const cardSchedule = effective.cardBonusSchedule
  const fmtLimit = (value: number | null | undefined) =>
    value === null || value === undefined ? 'Unlimited' : String(value)

  const cardInfo = cardBonusScheduleInfo(cardSchedule)
  const teams = teamsConfigOf(config)
  const seatCount = Number(players)
  const teamCount = teams && Number.isInteger(seatCount) && seatCount % teams.size === 0 ? seatCount / teams.size : null
  const specialRules = map?.specialRules ?? []

  return (
    <View className="gap-4">
      {specialRules.length > 0 && (
        <View
          className={bare
            ? 'pb-3 border-b border-olive/50'
            : 'bg-paper border-2 border-olive rounded-xl px-4 py-3'}
        >
          <Text className="text-ink font-stencil uppercase text-sm mb-1">Special Rules</Text>
          {specialRules.map((rule) => (
            <View key={rule} className="flex-row items-start mt-1">
              <Text className="text-ink-soft font-typed text-sm mr-2">•</Text>
              <Text className="text-ink-soft font-typed text-sm flex-1">{rule}</Text>
            </View>
          ))}
        </View>
      )}

      <View className={bare ? '' : 'bg-paper border-2 border-olive rounded-xl px-4 py-2'}>
        {isWarfishClassicsMap(map)
          ? <WarfishClassicsMapRow name={map?.name ?? 'Unknown map'} />
          : <ConfigRow label="Map" value={map?.name ?? 'Unknown map'} />}
        <ConfigRow label="Territories" value={String(map?.territories.length ?? 0)} />
        <ConfigRow label="Players" value={players} />
        {/* Only team games get the row, so free-for-all rules are unchanged. */}
        {teams && (
          <ConfigRow
            label="Teams"
            value={teamsRuleValue(teams)}
            hint={teamCount ? teamListLabel(teamCount) : 'equal teams'}
          />
        )}
        <ConfigRow
          label="Starting troops"
          value={String(startingTroops)}
          hint="per territory"
        />
        {/* Only fog games get the row, so other games' rules are unchanged. */}
        {fogOfWarOf(config) !== 'none' && (
          <ConfigRow
            label="Fog of war"
            value={fogOfWarOf(config) === 'light' ? 'Light' : 'Heavy'}
            hint={fogOfWarOf(config) === 'light' ? 'colours shown, troops hidden' : 'nothing beyond your borders'}
          />
        )}
        <ConfigRow
          label="Territory claim"
          value={autoAssign ? 'Random' : 'Manual claim'}
          hint={
            autoAssign
              ? 'Server distributes territories evenly'
              : 'Players take turns claiming one at a time'
          }
        />
        <ConfigRow label="Attacks / turn" value={fmtLimit(maxAttacks)} />
        <ConfigRow
          label="Fortifies / turn"
          value={fmtLimit(maxFortifies)}
          hint="classic ruleset: 1"
        />
        <ConfigRow
          label="Reinforcements"
          value="max(3, owned ÷ 3)"
          hint="plus region bonuses below"
        />
        <ConfigRow
          label="Eliminate bonus"
          value={`+${eliminateBonus} armies`}
          hint="plus all of their cards"
        />
        <ConfigRow
          label="Card schedule"
          value={cardInfo.label}
          hint={cardInfo.sequence}
        />
        <ConfigRow label="Resigning" value="Becomes neutral" last={bootAfterSeconds === undefined} />
        {bootAfterSeconds !== undefined && <ConfigRow label="Boot after" value={BOOT_TIMER_OPTIONS.find(option => option.seconds === bootAfterSeconds)?.label ?? '5 days'} last />}
      </View>
    </View>
  )
}
